import {RecursoPesquisa} from "@components/recurso/RecursoPesquisa.tsx";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {CreateRecurso} from "@localization/siteMap/endpoints/_RecursosEndpoints.ts";
import type {INovaRecursoDto} from "@models/recurso/dto.ts";
import type {IRecurso} from "@models/recurso/IRecurso.ts";
import {SaveOutlined} from "@mui/icons-material";
import {Button, FormControlLabel, Grid, Switch, TextField} from "@mui/material";
import {ToMoney} from "@shared/MathHelp";
import {MoneyInputEntry} from "@shared/specialComponents/InputEntry.tsx";
import dayjs from "dayjs";
import React, {useEffect, useState} from "react";

interface CreateRecursoComponentProps {
    onCreate?: (v: IRecurso) => void
}

export function CreateRecursoComponent({onCreate}: CreateRecursoComponentProps) {
    const {createAlertFromResponse} = useAlert()
    const [recursoBase, setRecursoBase] = useState<IRecurso | undefined>(undefined)
    const [recurso, setRecurso] = useState<INovaRecursoDto>({
        id: 0,
        nome: '',
        descricao: '',
        valor: 0,
        isAtivo: true,
        dataInicio: dayjs(),
        dataFim: dayjs().add(1, 'year'),
    })
    const [confirmouValorExcedido, setConfirmouValorExcedido] = useState(false)

    useEffect(() => {
        setRecurso({...recurso, antecessorId: recursoBase?.id ?? 0})
    }, [recursoBase]);

    useEffect(() => {
        if (valorExcedido > 0) {
            setConfirmouValorExcedido(false)
        }
    }, [recursoBase, recurso])

    const handleCreateRecurso = () => {
        CreateRecurso(recurso, confirmouValorExcedido).then((x) => {
            createAlertFromResponse(x)

            if (x.status == 'Success') {
                setRecurso({
                    id: 0,
                    nome: '',
                    descricao: '',
                    valor: 0,
                    isAtivo: true,
                    dataInicio: dayjs(),
                    dataFim: dayjs().add(30, 'day'),
                })
                if (onCreate) onCreate(x.items)
            }
        })
    }

    const valorExcedido = (recurso.valor) - (recursoBase?.valor ?? recurso.valor);

    return (
        <Grid size={12} p={1.5}>
            <Grid container spacing={1.5} columnSpacing={.5}>
                <Grid size={12}>
                    <TextField fullWidth
                               label={'Nome'}
                               value={recurso.nome}
                               onChange={(e) => {
                                   setRecurso({
                                       ...recurso,
                                       nome: e.target.value
                                   })
                               }}/>
                </Grid>
                <Grid size={12}>
                    <TextField multiline
                               fullWidth
                               label={'Descrição'}
                               value={recurso.descricao}
                               onChange={(e) => {
                                   setRecurso({
                                       ...recurso,
                                       descricao: e.target.value
                                   })
                               }}/>
                </Grid>
                <Grid size={12}>
                    <MoneyInputEntry onChange={v => setRecurso({...recurso, valor: v || 0})} value={recurso.valor}
                                     textFieldProps={{
                                         error: (recurso.valor < 0 || (valorExcedido > 0 && !confirmouValorExcedido)) ?? false,
                                         helperText: recurso.valor < 0 ? 'Valor não pode ser negativo' : valorExcedido > 0 && !confirmouValorExcedido ? `Valor excedido, selecione a opção "Desejo incrementar a recurso base para continuar"` : ''
                                     }}
                                     label={'Valor inicial do Recurso'}/>
                </Grid>
                <Grid size={6}>
                    <TextField fullWidth
                               label={'Vigência Inicial'}
                               type={'date'}
                               error={recurso.dataInicio.isAfter(recurso.dataFim)}
                               helperText={'Entra em vigencia em ' + recurso.dataInicio.diff(dayjs(dayjs().format('MM/DD/YYYY')), 'day') + ' dia(s)'}
                               value={recurso.dataInicio.format('YYYY-MM-DD')}
                               onChange={(e) => {
                                   setRecurso({
                                       ...recurso,
                                       dataInicio: dayjs(e.target.value)
                                   })
                               }}/>
                </Grid>
                <Grid size={6}>
                    <TextField fullWidth
                               label={'Vigência Final'}
                               type={'date'}
                               error={recurso.dataFim.isBefore(recurso.dataInicio)}
                               helperText={'Vigencia dura ' + recurso.dataFim.diff(recurso.dataInicio, 'day') + ' dia(s)'}
                               value={recurso.dataFim.format('YYYY-MM-DD')}
                               onChange={(e) => {
                                   setRecurso({
                                       ...recurso,
                                       dataFim: dayjs(e.target.value)
                                   })
                               }}/>
                </Grid>
                <Grid size={12}>
                    <RecursoPesquisa display={{name: 'Recurso base'}}
                                     onItemSelected={(x) => {
                                         setRecursoBase(x)
                                         // if (c && c.length > 0) {
                                         //     setValorMaximo(c[0].value ?? 0)
                                         // }
                                     }}/>
                </Grid>
                {valorExcedido > 0 && (
                    <FormControlLabel control={<Switch/>}
                                      checked={confirmouValorExcedido}
                                      label={`Desejo incrementar a recurso base em ${ToMoney(valorExcedido)} para continuar`}
                                      onChange={(_, v) => {
                                          setConfirmouValorExcedido(v)
                                      }}/>
                )
                }

                <Grid size={12}>
                    <Button endIcon={<SaveOutlined/>}
                            variant={'contained'}
                            color={'success'}
                            fullWidth
                            onClick={handleCreateRecurso}>
                        Criar recurso
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
}